// Utility functions for application settings

// AI Disabled State
export const useAIDisabled = (): boolean => {
  // Retrieve from localStorage or default to false
  const storedValue = localStorage.getItem('ai-disabled');
  return storedValue === 'true';
};

export const setAIDisabled = (value: boolean): void => {
  localStorage.setItem('ai-disabled', String(!!value));
};

// Dev Mode State
export const useDevMode = (): boolean => {
  // Retrieve from localStorage or default to false
  const storedValue = localStorage.getItem('dev-mode');
  return storedValue === 'true';
};

export const setDevMode = (value: boolean): void => {
  localStorage.setItem('dev-mode', String(!!value));
};

// Color Mode Utilities
export const useColorMode = (): string => {
  return localStorage.getItem('color-mode') || 'light';
};

export const setColorMode = (value: string): void => {
  localStorage.setItem('color-mode', value);
};

// Custom Color Utilities
export const useCustomColor = (): string => {
  return localStorage.getItem('custom-color') || '#0E2356';
};

export const setCustomColor = (value: string): void => {
  localStorage.setItem('custom-color', value);
};

// Date Format Utilities
export type DateFormatOption = {
  id: string;
  name: string;
  format: string;
  example: string;
};

export const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  {
    id: 'MMM-d-yyyy',
    name: 'Month Day, Year',
    format: 'MMM d, yyyy',
    example: 'Apr 9, 2025'
  },
  {
    id: 'MM-dd-yyyy',
    name: 'MM/DD/YYYY',
    format: 'MM/dd/yyyy',
    example: '04/09/2025'
  },
  {
    id: 'dd-MM-yyyy',
    name: 'DD/MM/YYYY',
    format: 'dd/MM/yyyy',
    example: '09/04/2025'
  },
  {
    id: 'yyyy-MM-dd',
    name: 'YYYY-MM-DD',
    format: 'yyyy-MM-dd',
    example: '2025-04-09'
  }
];

// Time Format Utilities
export type TimeFormatOption = {
  id: string;
  name: string;
  format: string;
  example: string;
};

export const TIME_FORMAT_OPTIONS: TimeFormatOption[] = [
  {
    id: 'hh-mm-a',
    name: '12-hour',
    format: 'hh:mm a',
    example: '01:05 PM'
  },
  {
    id: 'HH-mm',
    name: '24-hour',
    format: 'HH:mm',
    example: '13:05'
  },
  {
    id: 'h-mm-a',
    name: '12-hour (no leading zero)',
    format: 'h:mm a',
    example: '1:05 PM'
  },
  {
    id: 'HH-mm-ss',
    name: '24-hour with seconds',
    format: 'HH:mm:ss',
    example: '13:05:30'
  }
];

// Timezone options
export type TimezoneOption = {
  id: string;
  name: string;
  offset: string;
};

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { id: 'UTC', name: 'UTC', offset: '+00:00' },
  { id: 'America/New_York', name: 'Eastern Time (US & Canada)', offset: '-05:00' },
  { id: 'America/Chicago', name: 'Central Time (US & Canada)', offset: '-06:00' },
  { id: 'America/Denver', name: 'Mountain Time (US & Canada)', offset: '-07:00' },
  { id: 'America/Los_Angeles', name: 'Pacific Time (US & Canada)', offset: '-08:00' },
  { id: 'Europe/London', name: 'London', offset: '+00:00' },
  { id: 'Europe/Paris', name: 'Paris', offset: '+01:00' },
  { id: 'Europe/Berlin', name: 'Berlin', offset: '+01:00' },
  { id: 'Asia/Tokyo', name: 'Tokyo', offset: '+09:00' },
  { id: 'Asia/Shanghai', name: 'Shanghai', offset: '+08:00' },
  { id: 'Australia/Sydney', name: 'Sydney', offset: '+10:00' }
];

// Working hours settings
export const useWorkingHours = (): { startHour: number; endHour: number } => {
  const startHour = parseInt(localStorage.getItem('working-hours-start') || '9');
  const endHour = parseInt(localStorage.getItem('working-hours-end') || '17');
  return { startHour, endHour };
};

export const setWorkingHours = (startHour: number, endHour: number): void => {
  localStorage.setItem('working-hours-start', startHour.toString());
  localStorage.setItem('working-hours-end', endHour.toString());
};

export const useTimezone = (): string => {
  return localStorage.getItem('timezone') || 'UTC';
};

export const setTimezone = (value: string): void => {
  localStorage.setItem('timezone', value);
};

export const useDateFormat = (): string => {
  return localStorage.getItem('date-format') || DATE_FORMAT_OPTIONS[0].id;
};

export const setDateFormat = (value: string): void => {
  localStorage.setItem('date-format', value);
};

export const useTimeFormat = (): string => {
  return localStorage.getItem('time-format') || TIME_FORMAT_OPTIONS[0].id;
};

export const setTimeFormat = (value: string): void => {
  localStorage.setItem('time-format', value);
};

// Outdated Threshold Utilities
export const useOutdatedThreshold = (): number => {
  const storedValue = localStorage.getItem('outdated-threshold');
  return storedValue ? parseInt(storedValue, 10) : 3; // Default to 3 years
};

export const setOutdatedThreshold = (value: number): void => {
  localStorage.setItem('outdated-threshold', value.toString());
};

// Sidebar Order
export const useSidebarOrder = (): string[] | null => {
  const stored = localStorage.getItem('sidebar-order');
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const setSidebarOrder = (value: string[] | null): void => {
  if (value === null)
    localStorage.removeItem('sidebar-order');
  else
    localStorage.setItem('sidebar-order', JSON.stringify(value));
};

// Advanced Search View Mode
export const useAdvancedSearchViewMode = (): string => {
  return localStorage.getItem('advanced-search-view-mode') || 'card';
};

export const setAdvancedSearchViewMode = (value: string): void => {
  localStorage.setItem('advanced-search-view-mode', value);
};

export const getFormattedDateTime = (
  date: Date | string | null | undefined,
  dateFormatId?: string,
  timeFormatId?: string,
  showTime: boolean = true
): string => {
  // If date is undefined, null, or invalid, return a placeholder
  if (!date) return "Not specified";
  
  // Try to create a date object
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return "Invalid date";
  }
  
  // Use provided formats or get from localStorage
  const selectedDateFormat = dateFormatId || useDateFormat();
  const selectedTimeFormat = timeFormatId || useTimeFormat();
  
  // Find the format strings
  const dateFormat = DATE_FORMAT_OPTIONS.find(opt => opt.id === selectedDateFormat)?.format || 'MMM d, yyyy';
  const timeFormat = TIME_FORMAT_OPTIONS.find(opt => opt.id === selectedTimeFormat)?.format || 'hh:mm a';
  
  try {
    // Format the date using Intl.DateTimeFormat for better localization support
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: dateFormat.includes('MMM') ? 'short' : '2-digit',
      day: '2-digit',
      hour: timeFormat.includes('HH') ? '2-digit' : 'numeric',
      minute: '2-digit',
      second: timeFormat.includes('ss') ? '2-digit' : undefined,
      hour12: timeFormat.includes('a')
    });
    
    const parts = dateFormatter.formatToParts(dateObj);
    
    // Extract date components
    const month = parts.find(part => part.type === 'month')?.value || '';
    const day = parts.find(part => part.type === 'day')?.value || '';
    const year = parts.find(part => part.type === 'year')?.value || '';
    
    // Extract time components
    const hour = parts.find(part => part.type === 'hour')?.value || '';
    const minute = parts.find(part => part.type === 'minute')?.value || '';
    const second = parts.find(part => part.type === 'second')?.value || '';
    const dayPeriod = parts.find(part => part.type === 'dayPeriod')?.value || '';
    
    // Format date part based on selected format
    let formattedDate = '';
    if (dateFormat.includes('MMM')) {
      formattedDate = `${month} ${day}, ${year}`;
    } else if (dateFormat.startsWith('MM')) {
      formattedDate = `${month}/${day}/${year}`;
    } else if (dateFormat.startsWith('dd')) {
      formattedDate = `${day}/${month}/${year}`;
    } else {
      formattedDate = `${year}-${month}-${day}`;
    }
    
    // Only add time if requested
    if (showTime) {
      // Format time part based on selected format
      let formattedTime = '';
      if (timeFormat.includes('HH')) {
        formattedTime = timeFormat.includes('ss') 
          ? `${hour}:${minute}:${second}` 
          : `${hour}:${minute}`;
      } else {
        formattedTime = `${hour}:${minute} ${dayPeriod}`;
      }
      
      return `${formattedDate} at ${formattedTime}`;
    }
    
    return formattedDate;
  } catch (err) {
    console.error("Date formatting error:", err);
    return "Date formatting error";
  }
};
